import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@/shared/components/Button/Button'
import IconButton from '@/shared/components/IconButton/IconButton'
import ConfirmationModal from '@/shared/components/Modal/ConfirmationModal/ConfirmationModal'
import Select from '@/shared/components/Select/Select'
import TextField from '@/shared/components/TextField/TextField'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { downloadBlob } from '@/shared/utils/downloadBlob'
import { useFloorPlanStore } from '../../store/useFloorPlanStore'
import { useFloorPlanMutations } from '../../hooks/useFloorPlanMutations'
import RoomManageModal from '../RoomManageModal/RoomManageModal'
import SavePlanButton from '../SavePlanButton/SavePlanButton'
import {
  FLOOR_PLAN_SCHEMA_VERSION,
  FloorTable,
  Room,
  TableShape
} from '../../types/floorPlan.model'
import styles from './FloorPlanToolbar.module.scss'

const MIN_SEATS = 1
const MAX_SEATS = 16
const DEFAULT_SEATS = 4

function clampSeats(value: number): number {
  if (Number.isNaN(value)) return MIN_SEATS
  return Math.min(MAX_SEATS, Math.max(MIN_SEATS, value))
}

interface FloorPlanToolbarProps {
  selectedTableIds: string[]
  onClear: () => void
}

function FloorPlanToolbar(props: FloorPlanToolbarProps) {
  const { selectedTableIds, onClear } = props
  const { t } = useTranslation()

  const shapeOptions: OptionType[] = useMemo(
    () => [
      { value: 'round', label: t('FLOOR_PLAN.SHAPE.ROUND') },
      { value: 'square', label: t('FLOOR_PLAN.SHAPE.SQUARE') },
      { value: 'rectangle', label: t('FLOOR_PLAN.SHAPE.RECTANGLE') }
    ],
    [t]
  )

  const [newShape, setNewShape] = useState<OptionType>(shapeOptions[0])
  const [newSeats, setNewSeats] = useState<string>(String(DEFAULT_SEATS))

  // Room management (add / rename / delete) now lives inside the panel.
  const [roomModalMode, setRoomModalMode] = useState<
    'create' | 'rename' | null
  >(null)
  const rooms = useFloorPlanStore(s => s.rooms)
  const activeRoomId = useFloorPlanStore(s => s.activeRoomId)
  const activeRoom = rooms.find(room => room.id === activeRoomId) ?? null
  const activeRoomTableCount = useFloorPlanStore(
    s => s.tables.filter(table => table.roomId === activeRoomId).length
  )
  // A room goes with all its tables, so the trash icon asks first.
  const [isDeleteRoomOpen, setIsDeleteRoomOpen] = useState(false)

  // Single-table editing only applies when exactly one table is selected.
  const singleSelectedId =
    selectedTableIds.length === 1 ? selectedTableIds[0] : null

  const selectedTable = useFloorPlanStore(s =>
    singleSelectedId
      ? (s.tables.find(table => table.id === singleSelectedId) ?? null)
      : null
  )

  const snapEnabled = useFloorPlanStore(s => s.snapEnabled)
  const toggleSnap = useFloorPlanStore(s => s.toggleSnap)
  const tool = useFloorPlanStore(s => s.tool)
  const setTool = useFloorPlanStore(s => s.setTool)
  const undo = useFloorPlanStore(s => s.undo)
  const redo = useFloorPlanStore(s => s.redo)
  const canUndo = useFloorPlanStore(s => s.past.length > 0)
  const canRedo = useFloorPlanStore(s => s.future.length > 0)
  const importPlan = useFloorPlanStore(s => s.importPlan)
  const { showSnackbar } = useSnackbar()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Save = download the current plan as a JSON file the user can keep / re-import.
  const handleSavePlan = () => {
    const { rooms, tables } = useFloorPlanStore.getState()
    const payload = { version: FLOOR_PLAN_SCHEMA_VERSION, rooms, tables }
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json'
    })
    downloadBlob(blob, 'plan-sali.json')
  }

  const handleLoadPlanClick = () => fileInputRef.current?.click()

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-importing the same file
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as {
          rooms?: Room[]
          tables?: FloorTable[]
        }
        if (!Array.isArray(parsed.rooms) || !Array.isArray(parsed.tables)) {
          throw new Error('Invalid plan file')
        }
        importPlan(parsed.rooms, parsed.tables)
        onClear()
        showSnackbar(
          t('FLOOR_PLAN.ALERTS.IMPORT_SUCCESS.TITLE'),
          t('FLOOR_PLAN.ALERTS.IMPORT_SUCCESS.DESCRIPTION'),
          'success'
        )
      } catch {
        showSnackbar(
          t('FLOOR_PLAN.ALERTS.IMPORT_ERROR.TITLE'),
          t('FLOOR_PLAN.ALERTS.IMPORT_ERROR.DESCRIPTION'),
          'error'
        )
      }
    }
    reader.readAsText(file)
  }

  // Ctrl/Cmd+Z to undo, Ctrl/Cmd+Shift+Z to redo (ignored while typing).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return
      if (event.key.toLowerCase() !== 'z') return
      const target = event.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) {
        return
      }
      event.preventDefault()
      if (event.shiftKey) redo()
      else undo()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [undo, redo])

  const {
    addTable,
    setTableSeatCount,
    setTableShape,
    setTableLabel,
    deleteTable,
    addRoom,
    renameRoom,
    deleteRoom
  } = useFloorPlanMutations()

  const handleRoomConfirm = (name: string) => {
    if (roomModalMode === 'create') addRoom(name)
    else if (roomModalMode === 'rename' && activeRoom)
      renameRoom(activeRoom.id, name)
  }

  const handleRoomDelete = () => {
    if (activeRoom) deleteRoom(activeRoom.id)
    setIsDeleteRoomOpen(false)
  }

  const handleAddTable = () => {
    const seats = clampSeats(parseInt(newSeats, 10))
    if (!addTable((newShape.value as TableShape) ?? 'round', seats))
      showSnackbar(
        t('FLOOR_PLAN.ALERTS.NO_SPACE.TITLE'),
        t('FLOOR_PLAN.ALERTS.NO_SPACE.DESCRIPTION'),
        'warning'
      )
  }

  const handleNewSeatsChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setNewSeats(e.target.value)

  const handleSelectedSeatsChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!selectedTable) return
    const seats = clampSeats(parseInt(e.target.value, 10))
    setTableSeatCount(selectedTable.id, seats)
  }

  const handleSelectedShapeChange = (value: OptionType | null) => {
    if (!selectedTable || !value) return
    setTableShape(selectedTable.id, value.value as TableShape)
  }

  const handleSelectedLabelChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!selectedTable) return
    setTableLabel(selectedTable.id, e.target.value)
  }

  const handleDelete = () => {
    if (!selectedTable) return
    deleteTable(selectedTable.id)
    onClear()
  }

  const handleDeleteSelected = () => {
    selectedTableIds.forEach(id => deleteTable(id))
    onClear()
  }

  const selectedShapeOption =
    shapeOptions.find(option => option.value === selectedTable?.shape) ?? null

  // Hidden file input is always mounted so Load works in either panel state.
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="application/json,.json"
      onChange={handleFileChange}
      style={{ display: 'none' }}
    />
  )

  const roomModal = (
    <RoomManageModal
      isOpen={roomModalMode !== null}
      mode={roomModalMode ?? 'create'}
      initialName={roomModalMode === 'rename' ? (activeRoom?.name ?? '') : ''}
      onClose={() => setRoomModalMode(null)}
      onConfirm={handleRoomConfirm}
    />
  )

  return (
    <div className={styles.groups}>
      {/* Icons rather than a stack of wordy buttons; each names itself on
          hover. Save keeps its word: it is the one action that matters. */}
      <section className={styles.section}>
        <span className={styles.sectionTitle}>
          {t('FLOOR_PLAN.TOOLBAR.GROUP_ROOMS')}
        </span>
        <div className={styles.iconRow}>
          <IconButton
            icon="las la-plus icon-big"
            color="primary"
            tooltip={t('FLOOR_PLAN.ROOM.ADD')}
            onClick={() => setRoomModalMode('create')}
          />
          <IconButton
            icon="las la-edit icon-big"
            color="primary"
            tooltip={t('FLOOR_PLAN.ROOM.RENAME')}
            disabled={!activeRoom}
            onClick={() => setRoomModalMode('rename')}
          />
          <IconButton
            icon="las la-trash icon-big"
            color="primary"
            tooltip={t('FLOOR_PLAN.ROOM.DELETE')}
            disabled={rooms.length <= 1}
            onClick={() => setIsDeleteRoomOpen(true)}
          />
        </div>
      </section>

      <section className={styles.section} id="tour-floor-plan-plan-group">
        <span className={styles.sectionTitle}>
          {t('FLOOR_PLAN.TOOLBAR.GROUP_PLAN')}
        </span>
        <div className={styles.planRow}>
          {/* Persist the whole plan to the backend on demand. */}
          <SavePlanButton />
          {/* Local file utilities, export the plan to JSON / import it back. */}
          <IconButton
            icon="las la-download icon-big"
            color="primary"
            tooltip={t('FLOOR_PLAN.TOOLBAR.EXPORT_PLAN')}
            onClick={handleSavePlan}
          />
          <IconButton
            icon="las la-upload icon-big"
            color="primary"
            tooltip={t('FLOOR_PLAN.TOOLBAR.IMPORT_PLAN')}
            onClick={handleLoadPlanClick}
          />
        </div>
      </section>

      <section className={styles.section} id="tour-floor-plan-tools-group">
        <span className={styles.sectionTitle}>
          {t('FLOOR_PLAN.TOOLBAR.GROUP_TOOLS')}
        </span>
        <div className={styles.iconRow}>
          <IconButton
            icon="las la-mouse-pointer icon-big"
            color={tool === 'select' ? 'secondary' : 'primary'}
            variant="text"
            tooltip={t('FLOOR_PLAN.TOOLBAR.TOOL_SELECT')}
            onClick={() => setTool('select')}
          />
          <IconButton
            icon="las la-hand-paper icon-big"
            color={tool === 'hand' ? 'secondary' : 'primary'}
            variant="text"
            tooltip={t('FLOOR_PLAN.TOOLBAR.TOOL_HAND')}
            onClick={() => setTool('hand')}
          />
          {/* A mode like the two above, so it is lit the same way when on. */}
          <IconButton
            icon="las la-border-all icon-big"
            color={snapEnabled ? 'secondary' : 'primary'}
            variant="text"
            tooltip={t('FLOOR_PLAN.TOOLBAR.SNAP_TO_GRID')}
            onClick={toggleSnap}
          />
          <IconButton
            icon="las la-undo icon-big"
            color="primary"
            tooltip={t('FLOOR_PLAN.TOOLBAR.UNDO')}
            onClick={undo}
            disabled={!canUndo}
          />
          <IconButton
            icon="las la-redo icon-big"
            color="primary"
            tooltip={t('FLOOR_PLAN.TOOLBAR.REDO')}
            onClick={redo}
            disabled={!canRedo}
          />
        </div>
      </section>

      {/* "New table" and "Selection" are mutually exclusive: showing both made
          the identical shape/seats fields look duplicated. With nothing
          selected you configure the next table; selecting one switches the
          panel to editing that table. */}
      {selectedTableIds.length === 0 && (
        <section className={`${styles.section} ${styles.spacedTitle}`}>
          <span className={styles.sectionTitle}>
            {t('FLOOR_PLAN.TOOLBAR.GROUP_NEW_TABLE')}
          </span>
          <div className={styles.row}>
            <div className={styles.field}>
              <Select
                label={t('FLOOR_PLAN.TOOLBAR.SHAPE')}
                options={shapeOptions}
                value={newShape}
                hideClearIcon
                onChange={value => value && setNewShape(value as OptionType)}
              />
            </div>
            <div className={styles.seatsField}>
              <TextField
                label={t('FLOOR_PLAN.TOOLBAR.SEATS')}
                type="number"
                onlyInteger
                value={newSeats}
                onChange={handleNewSeatsChange}
              />
            </div>
          </div>
          {/* Sits with the fields it uses, so it's clear what gets added. */}
          <Button
            label={t('FLOOR_PLAN.TOOLBAR.ADD_TABLE')}
            variant="contained"
            color="primary"
            icon="las la-plus"
            onClick={handleAddTable}
          />
          <span className={styles.hint}>
            {t('FLOOR_PLAN.TOOLBAR.SELECT_HINT')}
          </span>
        </section>
      )}

      {selectedTableIds.length > 0 && (
        <section className={`${styles.section} ${styles.spacedTitle}`}>
          <span className={styles.sectionTitle}>
            {t('FLOOR_PLAN.TOOLBAR.GROUP_EDIT')}
          </span>

          {selectedTable && (
            <div className={styles.stack}>
              <div className={styles.row}>
                <div className={styles.seatsField}>
                  <TextField
                    label={t('FLOOR_PLAN.TOOLBAR.TABLE_NUMBER')}
                    type="number"
                    onlyInteger
                    value={selectedTable.label}
                    onChange={handleSelectedLabelChange}
                  />
                </div>
                <div className={styles.seatsField}>
                  <TextField
                    label={t('FLOOR_PLAN.TOOLBAR.SEATS')}
                    type="number"
                    onlyInteger
                    value={String(selectedTable.seatCount)}
                    onChange={handleSelectedSeatsChange}
                  />
                </div>
              </div>
              <div className={styles.field}>
                <Select
                  label={t('FLOOR_PLAN.TOOLBAR.SHAPE')}
                  options={shapeOptions}
                  value={selectedShapeOption}
                  hideClearIcon
                  onChange={value =>
                    handleSelectedShapeChange(value as OptionType | null)
                  }
                />
              </div>
              <Button
                label={t('FLOOR_PLAN.TOOLBAR.DELETE_TABLE')}
                variant="contained"
                color="secondary"
                icon="las la-trash"
                onClick={handleDelete}
              />
            </div>
          )}

          {selectedTableIds.length > 1 && (
            <div className={styles.stack}>
              <span className={styles.selectedCount}>
                {t('FLOOR_PLAN.TOOLBAR.SELECTED_COUNT', {
                  count: selectedTableIds.length
                })}
              </span>
              <Button
                label={t('FLOOR_PLAN.TOOLBAR.DELETE_SELECTED')}
                variant="outlined"
                color="primary"
                icon="las la-trash"
                onClick={handleDeleteSelected}
              />
            </div>
          )}
        </section>
      )}

      {fileInput}
      {roomModal}
      {/* Undo brings the room and its tables back, so this does not claim
          the deletion is for good. */}
      <ConfirmationModal
        isOpen={isDeleteRoomOpen}
        title={t('FLOOR_PLAN.ROOM.DELETE_CONFIRM.TITLE', {
          name: activeRoom?.name ?? ''
        })}
        message={
          activeRoomTableCount === 0
            ? t('FLOOR_PLAN.ROOM.DELETE_CONFIRM.EMPTY')
            : t('FLOOR_PLAN.ROOM.DELETE_CONFIRM.TABLES', {
                count: activeRoomTableCount
              })
        }
        onConfirm={handleRoomDelete}
        onClose={() => setIsDeleteRoomOpen(false)}
        resetsFormDirty={false}
        irreversible={false}
      />
    </div>
  )
}

export default FloorPlanToolbar
